// Authentication utility functions

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user' | 'volunteer';
  isActive: boolean;
}

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('token');
  const user = localStorage.getItem('user');
  return !!(token && user);
};

// Get current user from localStorage
export const getCurrentUser = (): User | null => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    return JSON.parse(userStr) as User;
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    return null;
  }
};

// Check if current user is admin
export const isAdmin = (): boolean => {
  const user = getCurrentUser();
  
  if (!user) {
    return false;
  }
  
  const isAdminRole = user.role === 'admin';
  // If isActive is undefined, assume true (for backward compatibility)
  const isUserActive = user.isActive === undefined ? true : user.isActive === true;
  
  return isAdminRole && isUserActive;
};

// Get authentication token
export const getToken = (): string | null => {
  return localStorage.getItem('token');
};

// Clear authentication data (logout)
export const clearAuth = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};

// Check if token is expired (basic check)
export const isTokenExpired = (): boolean => {
  const token = getToken();
  if (!token) return true;

  try {
    // Decode JWT token (basic check - you might want to add proper JWT validation)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

// Full authentication check including token expiration
export const isValidAuthentication = (): boolean => {
  return isAuthenticated() && !isTokenExpired();
};

// Full admin check including authentication validation
export const isValidAdmin = (): boolean => {
  return isValidAuthentication() && isAdmin();
};
